/*
 * PD Bundle Options (GRG-55) — custom (non-Shopify) bundle configurator.
 *
 * Renders Lens / Kit option tiles sourced from the `custom.len_options` and
 * `custom.kit_options` product-reference metafields (see snippet
 * `pd-bundle-options.liquid`). Each option is its own product; the tile shows
 * that product's own price, server-rendered and fixed (GRG-150).
 *
 * On selection it live-updates the Price block total + "Save $X" and the
 * Add-to-Cart button label. On submit, when any add-on is selected, it adds
 * the camera + selected lens + selected kit to the cart as SEPARATE line items
 * (grouped by a shared hidden `_bundle` property) via a single cart/add.js call,
 * then reuses the theme's existing cart-drawer rendering. With nothing selected
 * it stays out of the way and lets the standard product form add the base only.
 */
if (!customElements.get('pd-bundle-configurator')) {
  customElements.define(
    'pd-bundle-configurator',
    class PdBundleConfigurator extends HTMLElement {
      connectedCallback() {
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.basePrice = parseInt(this.dataset.basePrice, 10) || 0;
        // The host camera's own price. revertCombined() restores it after a
        // combined-product swap, and while swapped it is the camera's share of the
        // "bought separately" sum the saving is measured against.
        this.hostPrice = this.basePrice;
        this.savingClass = this.dataset.savingClass || '';

        this.groups = Array.from(this.querySelectorAll('[data-bundle-group]'));
        this.priceEl = this.dataset.priceEl ? document.getElementById(this.dataset.priceEl) : null;

        // Part B (combined product). When a selected lens or kit maps to a
        // "combined bundle" recipe, we swap the whole PD main product info to the
        // combined product (camera_with_lense), keeping this bundle block in place.
        this.sectionId = this.dataset.sectionId || '';
        this.combos = this.parseCombos();
        this._swappedHandle = null; // combined handle currently displayed, or null
        // Which option group ('lens' / 'kit') is folded into the combined product.
        // Only meaningful while _swappedHandle is set.
        this._swappedGroup = null;
        this._original = null; // captured base markup for revert

        // Click delegation for option selection.
        this.addEventListener('click', this.onOptionClick.bind(this));
        // Keyboard: arrow keys move within a radiogroup.
        this.addEventListener('keydown', this.onKeydown.bind(this));

        // The buy form lives in a sibling block; intercept its submit on a
        // common ancestor in the CAPTURE phase so this runs before the
        // product-form web component's at-target submit listener. The actual form
        // is resolved from the submit event (it changes after a combined swap).
        this.formRoot = this.closest('.product') || this.closest('.pd-main-product');
        if (this.formRoot) {
          this.formRoot.addEventListener('submit', this.onSubmit.bind(this), true);
        }

        // The SearchSpring "Select Accessories" block renders checkbox add-ons
        // outside the product wrapper, so we listen at document level to catch
        // them regardless of where in the DOM they appear.
        this._onAccessoryEvent = this.onAccessoryEvent.bind(this);
        document.addEventListener('change', this._onAccessoryEvent);
        document.addEventListener('click', this._onAccessoryEvent);

        // Variant change re-renders the base price + resets the ATC label, so keep
        // our base price in sync and re-apply the add-on total on top of it.
        this._onVariantChange = this.onVariantChange.bind(this);
        document.addEventListener('variant:change', this._onVariantChange);

        // Watch the price block: when the legacy accessory handler or the variant
        // fetch rewrites it, re-apply our total (disconnect/reconnect guards our own
        // writes against an observer feedback loop).
        this.startPriceObserver();

        // Render correct initial deltas + total (matches server render, no flash).
        this.recompute();
      }

      disconnectedCallback() {
        document.removeEventListener('variant:change', this._onVariantChange);
        document.removeEventListener('change', this._onAccessoryEvent);
        document.removeEventListener('click', this._onAccessoryEvent);
        if (this._priceObserver) this._priceObserver.disconnect();
      }

      /* ---------- money (shared helpers from assets/pd-money.js) ---------- */

      formatMoney(cents) {
        return window.pdFormatMoney(cents, this.moneyFormat);
      }

      // "Save $120", not "Save $120.00" — the cents are dropped only when zero.
      formatSaving(cents) {
        const str = this.formatMoney(cents);
        return cents % 100 === 0 ? str.replace(/[.,]00(?!\d)/, '') : str;
      }

      /* ---------- accessories (SearchSpring "Select Accessories") ---------- */

      // Sum of the checked accessory add-ons, in cents. Prefers data-price-cents
      // (raw integer, set by product-accessories-card.liquid) when present; falls
      // back to parsing data-price (formatted string) for SearchSpring's own
      // rendered markup which doesn't go through our Liquid snippet.
      accessoriesCents() {
        let sum = 0;
        document.querySelectorAll('[data-accessories-item]').forEach((el) => {
          const selected =
            el.classList.contains('active') || (typeof el.checked === 'boolean' && el.checked);
          if (!selected) return;
          const rawCents = parseInt(el.dataset.priceCents, 10);
          if (!isNaN(rawCents)) {
            sum += rawCents;
          } else {
            const dollars = parseFloat((el.getAttribute('data-price') || '').replace(/[^\d.]/g, ''));
            if (!isNaN(dollars)) sum += Math.round(dollars * 100);
          }
        });
        return sum;
      }

      onAccessoryEvent(evt) {
        if (
          evt.target.closest(
            '[data-accessories-item], .product-accessories__checkbox-label, [data-product-accessories-card]'
          )
        ) {
          this.scheduleRecompute();
        }
      }

      onVariantChange(evt) {
        const variant = evt.detail && evt.detail.variant;
        if (variant && typeof variant.price === 'number') {
          this.basePrice = variant.price;
          // Before a swap the variant is the host camera's; after one it is the
          // combined product's, and the camera's own price must not move.
          if (!this._swappedHandle) this.hostPrice = variant.price;
        }
        this.scheduleRecompute();
      }

      // Coalesce bursts of mutations/events into a single recompute on the next frame.
      scheduleRecompute() {
        if (this._recomputeScheduled) return;
        this._recomputeScheduled = true;
        requestAnimationFrame(() => {
          this._recomputeScheduled = false;
          this.recompute();
        });
      }

      startPriceObserver() {
        if (!this.priceEl || typeof MutationObserver === 'undefined') return;
        this._priceObserver = new MutationObserver(() => {
          if (this._writing) return;
          this.scheduleRecompute();
        });
        this._observePrice();
      }

      _observePrice() {
        if (this._priceObserver && this.priceEl) {
          this._priceObserver.observe(this.priceEl, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        }
      }

      /* ---------- selection ---------- */

      selectedOption(group) {
        return group.querySelector('[data-bundle-option][aria-checked="true"]');
      }

      onOptionClick(evt) {
        const option = evt.target.closest('[data-bundle-option]');
        if (!option || !this.contains(option)) return;
        if (option.dataset.unavailable === 'true') return;
        const group = option.closest('[data-bundle-group]');
        if (!group) return;
        this.setSelected(group, option);
      }

      onKeydown(evt) {
        if (!['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(evt.key)) return;
        const current = evt.target.closest('[data-bundle-option]');
        if (!current) return;
        const group = current.closest('[data-bundle-group]');
        const options = Array.from(group.querySelectorAll('[data-bundle-option]'));
        const idx = options.indexOf(current);
        const forward = evt.key === 'ArrowRight' || evt.key === 'ArrowDown';
        const step = forward ? 1 : options.length - 1;
        let next = null;
        for (let i = 1; i < options.length; i++) {
          const candidate = options[(idx + step * i) % options.length];
          if (candidate.dataset.unavailable !== 'true') { next = candidate; break; }
        }
        if (next) {
          evt.preventDefault();
          this.setSelected(group, next);
          // revealOption() scrolls the rail; native focus scrolling would also
          // jump the page.
          next.focus({ preventScroll: true });
        }
      }

      setSelected(group, option) {
        group.querySelectorAll('[data-bundle-option]').forEach((el) => {
          const isSel = el === option;
          el.setAttribute('aria-checked', isSel ? 'true' : 'false');
          el.classList.toggle('is-selected', isSel);
          el.tabIndex = isSel ? 0 : -1;
        });
        this.revealOption(option);
        this.recompute();
      }

      // Scroll the option rail just enough to show a part-hidden tile in full.
      // A tile that is already fully visible leaves the rail where it is.
      // Only the rail scrolls (never the page), and the rail's own padding (the
      // mobile gutter) counts as hidden so the tile lands on the text margin.
      revealOption(option) {
        const rail = option.closest('.pd-bundle__options');
        if (!rail || rail.scrollWidth <= rail.clientWidth) return;
        const style = getComputedStyle(rail);
        const railBox = rail.getBoundingClientRect();
        const box = option.getBoundingClientRect();
        const left = railBox.left + (parseFloat(style.paddingLeft) || 0);
        const right = railBox.right - (parseFloat(style.paddingRight) || 0);
        let delta = 0;
        if (box.left < left || box.width > right - left) delta = box.left - left;
        else if (box.right > right) delta = box.right - right;
        if (Math.abs(delta) < 1) return;
        // Round away from zero: scrollLeft snaps to whole pixels, so a fractional
        // delta rounded down left the last pixel of the tile hidden.
        delta = delta > 0 ? Math.ceil(delta) : Math.floor(delta);
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        rail.scrollBy({ left: delta, behavior: reduce ? 'auto' : 'smooth' });
      }

      /* ---------- recompute price ---------- */

      recompute() {
        let addonsPrice = 0;
        let foldedOptionPrice = 0;

        this.groups.forEach((group) => {
          const selected = this.selectedOption(group);
          if (!selected) return;
          const price = parseInt(selected.dataset.price, 10) || 0;
          // While swapped, the folded option (lens or kit) is already inside the
          // combined product, so its price is part of basePrice — adding it again
          // would double-count it. Mirrors onSubmit(), which omits its line item.
          if (this.isFolded(group.dataset.bundleGroup)) {
            foldedOptionPrice = price;
          } else {
            addonsPrice += price;
          }
        });

        const accessories = this.accessoriesCents();
        const total = this.basePrice + addonsPrice + accessories;
        // GRG-150: the saving is measured against Georges' own prices — what the
        // same items cost bought one by one — not compare-at, which GRG-148 hides.
        // Only a combined product is priced below its parts, so outside a swap
        // this equals the total and no saving shows (lens and kit go in the cart
        // as separate full-price lines).
        const separately = this._swappedHandle
          ? this.hostPrice + foldedOptionPrice + addonsPrice + accessories
          : total;

        // We are the single price authority in BOTH states. While swapped,
        // basePrice is the combined product and addons are whatever is still
        // selected on top of it (a kit) — previously this bailed out whenever
        // _swappedHandle was set, so a kit selected before the swap stayed in the
        // cart and in "What's included" but silently vanished from the displayed
        // price: PDP showed $6,749.00 while the cart charged $6,887.99 (#211).
        if (this.priceEl) {
          // Guard our own writes so the price-block observer doesn't loop on them.
          this._writing = true;
          if (this._priceObserver) this._priceObserver.disconnect();
          this.updatePriceBlock(total, separately);
          this.updateAtcButton(total);
          this._observePrice();
          this._writing = false;
        }

        // Broadcast the current add-on selection so the "What's included" list
        // can react.
        this.emitSelection();

        // Evaluate whether the current lens / kit triggers a combined-product swap.
        this.evaluateCombinedSwap();
      }

      /* ---------- selection broadcast (What's Included / combined product) ---------- */

      // The selected non-empty option in each group, as {group, handle}. The
      // What's Included list shows/hides server-rendered rows by handle, so it
      // needs nothing more (prices etc. are already in the rendered markup).
      getSelectionItems() {
        return this.groups
          .map((group) => {
            const sel = this.selectedOption(group);
            if (!sel || !sel.dataset.handle) return null; // "No lens"/"No kit"
            return { group: group.dataset.bundleGroup, handle: sel.dataset.handle };
          })
          .filter(Boolean);
      }

      emitSelection() {
        this.dispatchEvent(
          new CustomEvent('pd-bundle:selection', {
            bubbles: true,
            detail: { items: this.getSelectionItems() },
          })
        );
      }

      /* ---------- combined product swap (Part B) ----------
       *
       * ASSUMPTIONS (verify once the Admin MCP / live store is available):
       *  - The combos JSON is emitted by snippets/pd-bundle-options.liquid from
       *    product.metafields.custom.bundle_options (combined_bundle metaobjects:
       *    title, camera, lense, camera_with_lense). Only recipes whose `camera`
       *    is this product are included; mapped lens → camera_with_lense.
       *  - The combined product uses a template containing the SAME section id as
       *    this one, so /products/{handle}?section_id={sectionId} renders its PD
       *    main product. (If combined products use a different template this must
       *    be revisited.)
       *  - LIVE-VERIFY: after swapping the gallery + info markup, the gallery
       *    Swiper / variant tiles / wishlist etc. need re-initialising. We dispatch
       *    `shopify:section:load` (which the section framework uses in the editor);
       *    confirm on the live theme whether that re-inits the gallery, and extend
       *    reinitSection() if not.
       */

      parseCombos() {
        const el = this.querySelector('[data-bundle-combos]');
        if (!el) return [];
        try {
          const data = JSON.parse(el.textContent.trim());
          return Array.isArray(data) ? data : [];
        } catch (e) {
          return [];
        }
      }

      infoEl() {
        return this.formRoot ? this.formRoot.querySelector('.product__info-wrapper') : null;
      }

      galleryEl() {
        return this.formRoot ? this.formRoot.querySelector('.product__media-wrapper') : null;
      }

      // The product title lives in .product__info-header, a SIBLING of
      // .product__info-wrapper, so the info-column swap never reached it and the
      // page kept the base camera name while showing the combined price. We swap
      // only the title node: the sibling rating block is populated at runtime by
      // TestFreaks, and re-injecting its markup would blank the badge.
      titleEl() {
        return this.formRoot ? this.formRoot.querySelector('.product__title') : null;
      }

      // The gallery Swipers (main + vertical thumbs) are constructed inside the
      // minified assets/main.build.js, so there is no init entry point to call
      // again after we replace the gallery markup. Snapshot each instance's
      // passedParams once, then destroy / re-create with the global window.Swiper
      // (classic script, assets/swiper-bundle.min.js) around every swap.
      captureGallery(gallery) {
        const grab = (sel) => {
          const el = gallery && gallery.querySelector(sel);
          if (!el || !el.swiper || !el.swiper.passedParams) return null;
          const p = Object.assign({}, el.swiper.passedParams);
          delete p.el; // re-bound to the live node
          delete p.thumbs; // re-linked to the new thumbs instance
          return p;
        };
        return { main: grab('.product-main-slider'), thumbs: grab('.product-thumbs-slider') };
      }

      destroyGallery(gallery) {
        if (!gallery) return;
        ['.product-main-slider', '.product-thumbs-slider'].forEach((sel) => {
          const el = gallery.querySelector(sel);
          if (el && el.swiper) {
            try {
              el.swiper.destroy(true, true);
            } catch (e) {
              /* already torn down */
            }
          }
        });
      }

      initGallery(gallery, snap) {
        if (!gallery || !snap || typeof window.Swiper !== 'function') return;
        let thumbs = null;
        const thumbsEl = gallery.querySelector('.product-thumbs-slider');
        if (thumbsEl && snap.thumbs && !thumbsEl.swiper) {
          try {
            thumbs = new window.Swiper(thumbsEl, snap.thumbs);
          } catch (e) {
            thumbs = null;
          }
        }
        const mainEl = gallery.querySelector('.product-main-slider');
        if (mainEl && snap.main && !mainEl.swiper) {
          const params = Object.assign({}, snap.main);
          if (thumbs) params.thumbs = { swiper: thumbs };
          try {
            new window.Swiper(mainEl, params);
          } catch (e) {
            /* leave the static markup rather than throwing mid-swap */
          }
        }
      }

      // Replace the gallery markup while keeping its Swipers alive across the swap.
      swapGallery(gallery, html) {
        if (!gallery || html == null) return;
        this.destroyGallery(gallery);
        gallery.innerHTML = html;
        this.initGallery(gallery, this._original && this._original.swipers);
      }

      // True when `groupName`'s selected option is folded into the combined
      // product currently shown (so it has no line item / price of its own).
      isFolded(groupName) {
        return !!this._swappedHandle && this._swappedGroup === groupName;
      }

      // The recipe for the current selection, as {combo, group}. A bundle pairs
      // the camera with ONE option, so only one swap can apply at a time and
      // discounts never stack. When both the selected lens and the selected kit
      // have a bundle, the one that saves the customer more wins — saving =
      // (camera + that option, bought separately) − bundle price — and the other
      // option stays its own full-price line item. A tie goes to the lens.
      matchCombo() {
        let best = null;
        ['lens', 'kit'].forEach((groupName) => {
          const group = this.groups.find((g) => g.dataset.bundleGroup === groupName);
          const selected = group && this.selectedOption(group);
          const handle = selected && selected.dataset.handle;
          const combo = handle && this.combos.find((c) => c.optionHandle === handle);
          if (!combo) return;
          const optionPrice = parseInt(selected.dataset.price, 10) || 0;
          const saving = this.hostPrice + optionPrice - (Number(combo.price) || 0);
          if (!best || saving > best.saving) best = { combo, group: groupName, saving };
        });
        return best;
      }

      evaluateCombinedSwap() {
        if (!this.combos.length) return;
        const match = this.matchCombo();
        if (match) {
          if (this._swappedHandle !== match.combo.combinedHandle) {
            this._swappedGroup = match.group;
            this.swapToCombined(match.combo.combinedHandle);
          }
        } else if (this._swappedHandle) {
          this.revertCombined();
        }
      }

      // "Page apparatus" that lives inside the info column but must NOT be
      // duplicated by the swap: the bundle picker (+ its What's Included summary)
      // and the SearchSpring "Select Accessories" recommendations. These originals
      // are kept in place; the combined product's copies are never injected.
      isApparatus(el) {
        if (!el || el.nodeType !== 1) return false;
        if (el.matches('pd-bundle-configurator, pd-whats-included')) return true;
        return !!el.querySelector(
          'pd-bundle-configurator, pd-whats-included, script[type^="searchspring"], [data-accessories-item], [data-product-accessories-card]'
        );
      }

      // Record the original info-column layout so revert can rebuild it. Apparatus
      // children are stored by live-node reference (they never move/detach, so the
      // configurator's connectedCallback is never re-run); others by HTML.
      captureOriginal() {
        const info = this.infoEl();
        const gallery = this.galleryEl();
        if (!info) return;
        const seq = Array.from(info.children).map((child) =>
          this.isApparatus(child)
            ? { apparatus: true, node: child }
            : { apparatus: false, html: child.outerHTML }
        );
        const titleNode = this.titleEl();
        this._original = {
          galleryHTML: gallery ? gallery.innerHTML : null,
          titleHTML: titleNode ? titleNode.innerHTML : null,
          swipers: this.captureGallery(gallery),
          seq: seq,
        };
      }

      swapToCombined(handle) {
        const info = this.infoEl();
        const gallery = this.galleryEl();
        if (!info || !this.sectionId) return;

        if (!this._original) this.captureOriginal();
        this._swappedHandle = handle; // gate price writes immediately
        if (this._priceObserver) this._priceObserver.disconnect();

        fetch('/products/' + encodeURIComponent(handle) + '?section_id=' + encodeURIComponent(this.sectionId), {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        })
          .then((res) => (res.ok ? res.text() : Promise.reject(res.status)))
          .then((html) => {
            if (this._swappedHandle !== handle) return; // selection changed mid-flight
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const newInfo = doc.querySelector('.product__info-wrapper');
            const newGallery = doc.querySelector('.product__media-wrapper');
            if (!newInfo) {
              this._swappedHandle = null;
              return;
            }
            // Remove only the original NON-apparatus children (title/price/desc/buy
            // form/specs). Apparatus (bundle + What's Included + accessories) stays
            // in place — moving the live bundle node would re-run its connectedCallback.
            Array.from(info.children).forEach((child) => {
              if (!this.isApparatus(child)) child.remove();
            });

            // The bundle block (contains this configurator) is our position anchor:
            // the combined product's content is split around it so the live
            // apparatus keeps the slot the section's block order gives it.
            const bundleBlock = Array.from(info.children).find(
              (c) => c === this || c.contains(this)
            );
            const newChildren = Array.from(newInfo.children);
            const combinedChildren = newChildren.filter((c) => !this.isApparatus(c));

            // GRG-60 #222: where does the live apparatus belong in the rebuilt
            // column? It used to be hard-coded to "straight after the buy-buttons
            // block", which baked in the old block order (lens/kit below Add to
            // cart). Once the blocks were reordered to put lens/kit above the
            // price, a combo swap silently rebuilt the old order — so the fix held
            // until you picked a combo lens, then quietly undid itself.
            //
            // The anchor is now the section's block order, read from a document
            // that actually has a bundle block. Two sources, in order:
            //
            //   1. the FETCHED markup, when the combined product has lens/kit
            //      metafields of its own;
            //   2. this page's own pre-swap layout (_original.seq, captured before
            //      the non-apparatus children are removed).
            //
            // (1) alone is not enough, and that is the case that actually ships:
            // a combined product is usually a plain product with no lens/kit
            // metafields, so `pd-bundle-options` renders nothing for it and the
            // fetched markup has no bundle block to anchor on. Measured on the dev
            // theme with [PD - TEST] R5 II → "Canon EOS R5 II with RF 24-105mm":
            // the fetched column had no configurator, so (1) missed and the order
            // came back PRICE → BUY_BUTTONS → BUNDLES.
            const bundleIn = (c) =>
              (c.matches && c.matches('pd-bundle-configurator')) ||
              (c.querySelector && c.querySelector('pd-bundle-configurator'));

            // index among NON-apparatus children of the last one that precedes the
            // bundle block, i.e. the point to split the rebuilt content at
            const splitBefore = (nodes, isBundle, isApparatus) => {
              const i = nodes.findIndex(isBundle);
              return i === -1 ? null : nodes.slice(0, i).filter((n) => !isApparatus(n)).length - 1;
            };

            let splitIdx = splitBefore(newChildren, bundleIn, (c) => this.isApparatus(c));

            if (splitIdx === null && this._original && this._original.seq) {
              splitIdx = splitBefore(
                this._original.seq,
                (e) => e.apparatus && e.node && bundleIn(e.node),
                (e) => e.apparatus
              );
            }

            if (splitIdx === null) {
              // Neither document has a bundle block to anchor on. Last resort: the
              // original rule, hanging the apparatus off the ATC form.
              splitIdx = combinedChildren.findIndex(
                (c) =>
                  (c.matches && c.matches('product-form, .product-form')) ||
                  (c.querySelector &&
                    c.querySelector('product-form, [data-add-to-card-btn], form[data-type="add-to-cart-form"]'))
              );
              if (splitIdx === -1) splitIdx = combinedChildren.length - 1;
            }

            const before = document.createDocumentFragment();
            const after = document.createDocumentFragment();
            combinedChildren.forEach((child, i) => {
              (i <= splitIdx ? before : after).appendChild(child);
            });

            if (bundleBlock) {
              info.insertBefore(before, bundleBlock); // ATC + everything above it
              info.insertBefore(after, bundleBlock.nextSibling); // availability, specs, etc.
            } else {
              info.appendChild(before);
              info.appendChild(after);
            }

            const newTitle = doc.querySelector('.product__title');
            const titleNode = this.titleEl();
            if (newTitle && titleNode) titleNode.innerHTML = newTitle.innerHTML;

            if (newGallery && gallery) this.swapGallery(gallery, newGallery.innerHTML);

            // Adopt the combined product as the new base and TAKE BACK the price
            // block (it used to be set to null here, handing the price over). The
            // combined price comes from the combo recipe rather than being scraped
            // out of the injected markup; if the shopper then changes a variant on
            // the combined product, onVariantChange() updates basePrice from the
            // event as usual.
            const combo = this.combos.find((c) => c.combinedHandle === handle);
            if (combo && typeof combo.price === 'number') {
              this.basePrice = combo.price;
            }
            this.priceEl = this.dataset.priceEl
              ? document.getElementById(this.dataset.priceEl)
              : null;
            this._observePrice();
            this.reinitSection();
            this.recompute();
          })
          .catch(() => {
            this._swappedHandle = null;
          });
      }

      revertCombined() {
        const info = this.infoEl();
        const gallery = this.galleryEl();
        if (!info || !this._original) {
          this._swappedHandle = null;
          return;
        }
        // Drop the combined product's injected (non-apparatus) content.
        Array.from(info.children).forEach((child) => {
          if (!this.isApparatus(child)) child.remove();
        });
        // Re-insert the original non-apparatus children in their original positions,
        // anchored before the kept apparatus nodes (which never moved).
        const apparatusNodes = this._original.seq.filter((s) => s.apparatus).map((s) => s.node);
        let ai = 0;
        this._original.seq.forEach((item) => {
          if (item.apparatus) {
            ai += 1;
            return;
          }
          const tmp = document.createElement('template');
          tmp.innerHTML = item.html.trim();
          const node = tmp.content.firstElementChild;
          if (!node) return;
          const anchor = apparatusNodes[ai];
          if (anchor && anchor.parentNode === info) info.insertBefore(node, anchor);
          else info.appendChild(node);
        });
        const titleNode = this.titleEl();
        if (titleNode && this._original.titleHTML != null) {
          titleNode.innerHTML = this._original.titleHTML;
        }
        if (gallery && this._original.galleryHTML != null) {
          this.swapGallery(gallery, this._original.galleryHTML);
        }
        this._swappedHandle = null;
        // Back to the host product: restore the base the combined product replaced.
        this.basePrice = this.hostPrice;
        this.priceEl = this.dataset.priceEl ? document.getElementById(this.dataset.priceEl) : null;
        this._observePrice();
        this.reinitSection();
        this.recompute();
      }

      // Re-init after a markup swap. Custom elements in the swapped HTML (the buy
      // form <product-form>, variant picker) upgrade automatically on insertion, so
      // ATC works. The gallery Swipers are handled explicitly by swapGallery() —
      // see captureGallery/destroyGallery/initGallery above. We deliberately do NOT
      // broadcast shopify:section:load here, to avoid the SearchSpring recs
      // reprocessing into a duplicate block.
      reinitSection() {
        /* nothing left to broadcast — gallery is re-inited in swapGallery() */
      }

      updatePriceBlock(total, separately) {
        if (!this.priceEl) return;
        const root = this.priceEl.querySelector('.price');
        if (!root) return;
        const saving = separately - total;
        // GRG-172 (was GRG-165): a product tagged `show-rrp` keeps its "RRP" +
        // struck-through compare-at and its Save pill (snippets/price.liquid)
        // while the price is still its own price — nothing added on top, no
        // combined product swapped in. Once the total changes, the RRP no
        // longer describes what is being bought, so it falls through to the
        // bundle logic below; `price--bundle-saving` then hides the RRP
        // (assets/custom.css).
        const rrpSaving = parseInt(root.dataset.rrpSaving, 10);
        if (rrpSaving > 0 && !this._swappedHandle && total === this.basePrice) {
          root.classList.add('price--on-sale');
          root.classList.remove('price--bundle-saving');
          const totalStr = this.formatMoney(total);
          root.querySelectorAll('.price__regular .price-item--regular, .price__sale .price-item--sale').forEach(
            (el) => {
              el.textContent = totalStr;
            }
          );
          // A bundle saving may have rewritten (or created) the pill since
          // price.liquid rendered it; put the RRP pill back exactly as
          // price.liquid worded it — "Save $X", "Save X%", or no pill when the
          // product's save message is hidden.
          const rrpSaveText = root.dataset.rrpSaveText || '';
          const rrpPill = root.querySelector('.price__sale .price-item--savings');
          if (rrpPill && !rrpSaveText) rrpPill.remove();
          else if (rrpPill) rrpPill.textContent = rrpSaveText;
          return;
        }
        const onSale = saving > 0;
        root.classList.toggle('price--on-sale', onSale);
        // GRG-148 hides every compare-at price and sale pill with !important;
        // this class is the one exemption it makes (assets/custom.css), so a real
        // bundle saving still shows while the product's own compare-at stays
        // hidden.
        root.classList.toggle('price--bundle-saving', onSale);

        const totalStr = this.formatMoney(total);
        root.querySelectorAll('.price__regular .price-item--regular, .price__sale .price-item--sale').forEach(
          (el) => {
            el.textContent = totalStr;
          }
        );
        if (!onSale) return;

        const sale = root.querySelector('.price__sale');
        if (!sale) return;
        // Only the red "Save $X" pill carries the saving — no struck-through
        // "bought separately" price (client call, GRG-150). snippets/price.liquid
        // renders the pill only for a product with a compare-at price, so it may
        // not exist yet. Any struck compare-at price.liquid did render stays
        // hidden by GRG-148's rule in assets/custom.css.
        let pill = sale.querySelector('.price-item--savings');
        if (!pill) {
          pill = document.createElement('span');
          pill.className = ('price-item--savings badge price__badge-sale ' + this.savingClass).trim();
          sale.appendChild(pill);
        }
        pill.textContent = 'Save ' + this.formatSaving(saving);
      }

      updateAtcButton(total) {
        if (!this.formRoot) return;
        // Preferred path: dedicated span emitted by product-form.liquid.
        const priceEl = this.formRoot.querySelector('[data-add-to-card-btn] [data-atc-price]');
        if (priceEl) {
          priceEl.textContent = this.formatMoney(total);
          return;
        }
        // Fallback: Liquid not yet deployed — derive a regex from shop.money_format
        // so the replacement works for any currency prefix (€, £, AU$, etc.).
        const label = this.formRoot.querySelector('[data-add-to-card-btn] .product-form__submit__text');
        if (!label) return;
        const prefix = this.moneyFormat.replace(/\{\{[\s\S]*$/, '').trim();
        const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const re = new RegExp('[^\\s]*' + escaped + '[\\d,.]+');
        const updated = label.textContent.replace(re, this.formatMoney(total));
        if (updated !== label.textContent) label.textContent = updated;
      }

      /* ---------- add to cart (multi line item) ---------- */

      selectedVariantId(groupName) {
        const group = this.groups.find((g) => g.dataset.bundleGroup === groupName);
        if (!group) return null;
        const sel = this.selectedOption(group);
        return sel && sel.dataset.variantId ? sel.dataset.variantId : null;
      }

      onSubmit(evt) {
        // Use the form that was actually submitted — after a combined-product swap
        // the live form is the combined product's, not our cached reference.
        const form = evt.target;
        if (!form || form.tagName !== 'FORM') return;
        if (!form.matches('form[action*="/cart/add"], form[data-type="add-to-cart-form"]')) return;
        const idInput = form.querySelector('[name="id"]');
        if (!idInput || !idInput.value) return;

        // Cart model:
        //  - main product = the form's variant (the camera, or — when swapped — the
        //    combined product, which already includes the folded lens or kit).
        //  - lens / kit = its own line item unless it is the one folded into the
        //    combined product.
        const addons = [];
        ['lens', 'kit'].forEach((groupName) => {
          const variantId = this.selectedVariantId(groupName);
          if (variantId && !this.isFolded(groupName)) addons.push(variantId);
        });

        // Nothing extra to add → let the theme's product form add the single product.
        if (!addons.length) return;

        evt.preventDefault();
        evt.stopImmediatePropagation();
        this.addBundleToCart(form, idInput.value, addons);
      }

      addBundleToCart(form, mainId, addonVariants) {
        if (!mainId) return;
        this._activeForm = form;

        const submitButton = form.querySelector('[type="submit"]');
        const submitText = form.querySelector('.product-form__submit__text');
        const spinner = form.querySelector('.loading-overlay__spinner');
        if (submitButton) {
          submitButton.setAttribute('aria-disabled', 'true');
          submitButton.classList.add('loading');
        }
        // Hide the label while loading so the spinner sits centred (the spinner is
        // a sibling of the text; leaving the text visible pushes it off to the
        // side). Mirrors the theme's own add-to-cart loading state.
        if (submitText) submitText.classList.add('hidden');
        if (spinner) spinner.classList.remove('hidden');

        const bundleToken = 'bundle-' + mainId;
        const properties = { _bundle: bundleToken };
        const items = [{ id: mainId, quantity: 1, properties: properties }];
        addonVariants.forEach((id) => {
          items.push({ id: id, quantity: 1, properties: properties });
        });

        const cart =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const body = { items: items };
        if (cart && typeof cart.getSectionsToRender === 'function') {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          if (typeof cart.setActiveElement === 'function') {
            cart.setActiveElement(document.activeElement);
          }
        }

        fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.showFormError(response.description);
              return;
            }
            if (!cart) {
              window.location = window.routes.cart_url;
              return;
            }
            // The cart-drawer's open() reads `is-empty` off the drawer element
            // itself (renderContents only clears it from .drawer__inner). If the
            // cart started empty, leaving it set makes open() trap focus on the
            // now-removed empty container → "Cannot read properties of null". Clear
            // it before rendering, then auto-open the drawer (renderContents only
            // opens when its 2nd arg is true; cart-notification opens regardless).
            if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
            cart.renderContents(response, true);
          })
          .catch((e) => console.error(e))
          .finally(() => {
            if (submitButton) {
              submitButton.classList.remove('loading');
              submitButton.removeAttribute('aria-disabled');
            }
            if (submitText) submitText.classList.remove('hidden');
            if (spinner) spinner.classList.add('hidden');
          });
      }

      showFormError(message) {
        const form = this._activeForm;
        if (!form) return;
        const wrapper = form.querySelector('.product-form__error-message-wrapper');
        const text = form.querySelector('.product-form__error-message');
        if (!wrapper || !text) return;
        text.textContent = message || '';
        wrapper.toggleAttribute('hidden', !message);
      }
    }
  );
}
